import { List, Map, Set } from "immutable";
import { each, isEqual, map } from "lodash";

import { PropertyFormatIdentifier } from "../../constants/propertyFormats";
import SyncCustomFieldOptions from "../../constants/SyncCustomFieldOptions";
import syncLocalFields from "../../constants/syncLocalFields";
import syncUpdateActions from "../../constants/syncUpdateActions";
import combineDocumentAndFieldIds from "../../sync/utils/combineDocumentAndFieldIds";
import getAutoIgnoredRep from "../../sync/utils/getAutoIgnoredRep";
import getSyncableRep from "../../sync/utils/getSyncableRep";
import { createFromJS, immutableRecords } from "../../types";
import removeNodesFromTree from "../../utils/tree/removeNodesFromTree";
import combineReducersWithGlobal from "../combineReducersWithGlobal";
import { definitionActionTypes, definitionSelectors } from "../definition";
import typeSelectors from "../definition/types/selectors";
import itemSelectors from "../items/selectors";
import { actionTypes } from "./actions";
import syncSelectors from "./selectors";
import settings, { syncSettingsActionTypes, syncSettingsSelectors } from "./settings";

// When REPLACE_SETTINGS comes in, should we erase the settings data?
// Yes if syncing is disabled
// Yes if the application changes (no point retaining data between Scrivener and Ulysses)
// Yes if told to by the action
// No if we are just changing the project path legitimately
const shouldEraseSyncData = (action, currSettings) =>
  !action.settings.enabled || action.settings.app !== currSettings.app || action.eraseExistingData;

const filterLastSyncRepOnSettingsChange = (lastSyncRep, newSettings, globalState) => {
  const oldSettings = globalState.sync.settings;
  const localToExternalMap = globalState.sync.localToExternalIds;
  let eraseIds = [];

  // Go through the fields, and find any that have changed typeId
  oldSettings.entityFolders.forEach((oldEntityFolder, id) => {
    if (oldEntityFolder.folderId) {
      const newEntityFolder = newSettings.entityFolders.get(id);
      if (!newEntityFolder || newEntityFolder.mainType !== oldEntityFolder.mainType) {
        // We want to delete any IDs
        const localIds = itemSelectors.getItemIdsOfType(globalState, oldEntityFolder.mainType);
        const externalIds = localIds
          .map(l => localToExternalMap.get(l))
          .filter(eId => eId)
          .toArray();
        eraseIds.push(oldEntityFolder.folderId);
        eraseIds.push(...externalIds);
      }
    }
  });

  const result = lastSyncRep
    .update("documents", documents => documents.deleteAll(eraseIds))
    .update("documentTree", tree => removeNodesFromTree(tree, eraseIds));

  return result;
};

const lastSyncRep = (state = immutableRecords.SyncRepresentationRecord(), action, globalState) => {
  switch (action.type) {
    case actionTypes.UPDATE_EXTERNAL_AND_LAST_SYNC_REP: {
      const syncableRep = getSyncableRep(createFromJS.SyncRepresentationRecord(action.rep), globalState.sync.settings);
      const itemTypesById = syncSelectors.getItemTypesBySyncId(globalState);
      const autoIgnoreResult = getAutoIgnoredRep(
        syncableRep,
        globalState.sync.settings,
        typeSelectors.getTypesById(globalState),
        syncSelectors.getSyncIgnoreExternalIds(globalState),
        itemTypesById
      );
      return autoIgnoreResult.rep;
    }
    case syncSettingsActionTypes.REPLACE_SETTINGS:
      return shouldEraseSyncData(action, globalState.sync.settings)
        ? immutableRecords.SyncRepresentationRecord()
        : filterLastSyncRepOnSettingsChange(state, action.settings, globalState);

    default:
      return state;
  }
};

const status = (state = immutableRecords.SyncStatusRecord(), action, globalState) => {
  switch (action.type) {
    case actionTypes.UPDATE_SYNC_STATUS: {
      return action.status;
    }
    case syncSettingsActionTypes.REPLACE_SETTINGS:
      return shouldEraseSyncData(action, globalState.sync.settings) ? immutableRecords.SyncStatusRecord() : state;
    default:
      return state;
  }
};

const userActions = (state = Map(), action, globalState) => {
  switch (action.type) {
    case actionTypes.CHOOSE_LOCAL_SYNC_VALUE: {
      const combinedId = combineDocumentAndFieldIds(action.documentId, action.fieldId);
      const propUpdate = globalState.sync.status.documentUpdates.updatesById
        .get(action.documentId)
        .propertyUpdates.get(action.fieldId);
      return state.set(combinedId, propUpdate.set("action", syncUpdateActions.APPLY_LOCAL_TO_EXTERNAL));
    }
    case actionTypes.CHOOSE_EXTERNAL_SYNC_VALUE: {
      const combinedId = combineDocumentAndFieldIds(action.documentId, action.fieldId);
      const propUpdate = globalState.sync.status.documentUpdates.updatesById
        .get(action.documentId)
        .propertyUpdates.get(action.fieldId);
      return state.set(combinedId, propUpdate.set("action", syncUpdateActions.APPLY_EXTERNAL_TO_LOCAL));
    }
    case actionTypes.CLEAR_USER_ACTIONS:
      return Map();
    case syncSettingsActionTypes.REPLACE_SETTINGS:
      return shouldEraseSyncData(action, globalState.sync.settings) ? Map() : state;
    default:
      return state;
  }
};

const externalRep = (state = immutableRecords.SyncRepresentationRecord(), action, globalState) => {
  switch (action.type) {
    case actionTypes.UPDATE_EXTERNAL_REP:
    case actionTypes.UPDATE_EXTERNAL_AND_LAST_SYNC_REP: {
      // There are situations where we may receive the exact same data coming in from the externalRep
      // but we haven't yet checked to make sure it is unchanged.
      // So even though this is a slow-ish test, let's do it here so that we can prevent any unnecessary
      // updating of sync comparisons etc. being made continuously.
      const newRep = createFromJS.SyncRepresentationRecord(action.rep);
      return isEqual(state, newRep) ? state : newRep;
    }
    case syncSettingsActionTypes.REPLACE_SETTINGS:
      return shouldEraseSyncData(action, globalState.sync.settings) ? null : state;
    case actionTypes.SET_SYNC_FILE_MISSING:
      return null;
    default:
      return state;
  }
};

const fileMissing = (state = false, action) => {
  switch (action.type) {
    case actionTypes.UPDATE_EXTERNAL_REP:
    case actionTypes.UPDATE_EXTERNAL_AND_LAST_SYNC_REP:
      return false;
    case actionTypes.SET_SYNC_FILE_MISSING:
      return true;
    default:
      return state;
  }
};

const isScrivener2 = (state = false, action) => {
  switch (action.type) {
    case actionTypes.UPDATE_EXTERNAL_REP:
      return Boolean(action.isScrivener2);
    default:
      return state;
  }
};

const filterLocalToExternalIds = (localToExternalIds, externalRep, settings) => {
  if (localToExternalIds.size == 0 || !externalRep) {
    return localToExternalIds;
  }
  const rep = getSyncableRep(externalRep, settings);
  return localToExternalIds.filter(externalId => rep.documents && rep.documents.has(externalId));
};

const filterLocalIdsForChangedSettings = (localToExternalIds, newSettings, globalState) => {
  const oldSettings = globalState.sync.settings;
  let eraseIds = [];
  // Go through the fields, and find any that have changed tyeId
  oldSettings.fields.forEach((oldField, id) => {
    if (oldField.folderId) {
      const newField = newSettings.fields.get(id);
      if (!newField || newField.folderId !== oldField.folderId) {
        // We want to delete any IDs
        eraseIds.push(...itemSelectors.getItemIdsOfType(globalState, oldField.id).toArray());
      }
    }
  });
  const result = localToExternalIds.deleteAll(eraseIds);
  return result;
};

const filterLocalIdsOnSettingsChange = (localToExternalIds, newSettings, globalState) => {
  const result = filterLocalToExternalIds(localToExternalIds, globalState.sync.externalRep, newSettings);
  return filterLocalIdsForChangedSettings(result, newSettings, globalState);
};

const localToExternalIds = (state = Map(), action, globalState) => {
  switch (action.type) {
    case actionTypes.SET_LOCAL_TO_EXTERNAL_IDS: {
      const newPairs = Map(List(action.pairs).map(p => [p.localId, p.externalId]));
      return state.merge(newPairs);
    }
    case actionTypes.COMMIT_USER_CREATED_LINKS: {
      return state.merge(globalState.sync.userCreatedLinks);
    }
    case actionTypes.CLEAR_EXTERNAL_IDS_FOR_LOCAL_IDS:
      return state.deleteAll(action.localIds);
    case actionTypes.UPDATE_EXTERNAL_AND_LAST_SYNC_REP:
      return filterLocalToExternalIds(
        state,
        createFromJS.SyncRepresentationRecord(action.rep),
        globalState.sync.settings
      );
    case syncSettingsActionTypes.REPLACE_SETTINGS:
      return shouldEraseSyncData(action, globalState.sync.settings)
        ? Map()
        : filterLocalIdsOnSettingsChange(state, action.settings, globalState);
    default:
      return state;
  }
};

const filterColorOptions = (existingIds, colorsById) =>
  existingIds.filter((externalId, localId) => colorsById.has(localId));
const filterPropertyOptions = (existingIds, allowedValues) =>
  existingIds.filter((externalId, localId) => allowedValues.find(opt => opt.id === localId));

const filterCustomFieldOptionsOnDefinitionsChange = (customFieldIdsMap, newDefinitions) => {
  return customFieldIdsMap
    .map((existingIds, fieldId) => {
      // Handle colors by just filtering out those that match colors that no longer exist
      if (fieldId === syncLocalFields.color) {
        const colorsById = newDefinitions.colors.byId;
        return filterColorOptions(existingIds, colorsById);
      }
      // Handle properties by returning null for any where the property no longer exists or is no longer
      // a list, and filter the rest
      const propertiesById = definitionSelectors.getPropertiesById({ definitions: newDefinitions });
      const property = propertiesById.get(fieldId);
      if (!property || property.format !== PropertyFormatIdentifier.list) {
        return null;
      }
      const allowedValues = property.allowed ? property.allowed : Map();
      return filterPropertyOptions(existingIds, allowedValues);
    })
    .filter(existingIds => existingIds);
};

const filterCustomFieldOptionsOnSyncSettingsChange = (customFieldIdsMap, newSettings, globalState) => {
  const newFields = syncSettingsSelectors.getSyncCustomFieldsFromSettings(newSettings);
  const oldFields = syncSettingsSelectors.getSyncCustomFieldsFromSettings(
    syncSettingsSelectors.getSyncSettings(globalState)
  );
  return customFieldIdsMap
    .map((existingIds, fieldId) => {
      // If the field does not exist in the new settings, just remove the values
      if (!newFields.has(fieldId)) {
        return null;
      }
      // Otherwise, get the new and old external field id
      const newFieldId = newFields.get(fieldId).customFieldId;
      const oldFieldId = oldFields.has(fieldId) ? oldFields.get(fieldId).customFieldId : null;
      // If the settings have changed, so the field is pointing somewhere new, then we want to
      // erase all the old values
      if (newFieldId !== oldFieldId && oldFieldId !== SyncCustomFieldOptions.CREATE) {
        return Map();
      }
      // Otherwise, we are good to retain them
      return existingIds;
    })
    .filter(existingIds => existingIds);
};

const customFieldsIdsMap = (state = Map(), action, globalState) => {
  switch (action.type) {
    case actionTypes.SET_CUSTOM_FIELD_OPTION_IDS: {
      return state.withMutations(s => {
        each(action.idsMap, (newPairs, propId) => {
          if (s.has(propId)) {
            s.update(propId, oldPairs => oldPairs.merge(Map(newPairs)));
          } else {
            s.set(propId, Map(newPairs));
          }
        });
      });
    }
    case definitionActionTypes.REPLACE_DEFINITIONS:
      // On a Definitions (Timeline Settings) change, we need to weed out any values that
      // no longer exist
      return filterCustomFieldOptionsOnDefinitionsChange(state, action.definitions);
    case syncSettingsActionTypes.REPLACE_SETTINGS:
      return shouldEraseSyncData(action, globalState.sync.settings)
        ? Map()
        : filterCustomFieldOptionsOnSyncSettingsChange(state, action.settings, globalState);

    default:
      return state;
  }
};

const userCreatedLinks = (state = Map(), action, globalState) => {
  switch (action.type) {
    case actionTypes.ADD_USER_CREATED_LINKS: {
      const newPairs = Map(List(action.pairs).map(p => [p.localId, p.externalId]));
      return state.merge(newPairs);
    }
    case actionTypes.CLEAR_EXTERNAL_IDS_FOR_LOCAL_IDS:
    case actionTypes.CLEAR_USER_CREATED_LINKS_FOR_LOCAL_IDS:
      return state.deleteAll(action.localIds);
    case actionTypes.SYNC_IGNORE_EXTERNAL_IDS:
      return state.filter((externalId, _localId) => action.ids.indexOf(externalId) >= 0);
    case syncSettingsActionTypes.REPLACE_SETTINGS:
      return shouldEraseSyncData(action, globalState.sync.settings)
        ? Map()
        : filterLocalIdsOnSettingsChange(state, action.settings, globalState);
    case actionTypes.COMMIT_USER_CREATED_LINKS:
    case actionTypes.CLEAR_USER_ACTIONS:
      return Map();
    default:
      return state;
  }
};

const ignoreExternalIds = (state = Set(), action, globalState) => {
  switch (action.type) {
    case actionTypes.SYNC_IGNORE_EXTERNAL_IDS:
      return state.union(action.ids);
    case actionTypes.ADD_USER_CREATED_LINKS: {
      const externalIds = map(p => p.externalId);
      return state.subtract(externalIds);
    }
    case actionTypes.CLEAR_USER_CREATED_LINKS_FOR_LOCAL_IDS:
    case actionTypes.SYNC_UNIGNORE_EXTERNAL_IDS:
      return state.subtract(action.ids);

    case syncSettingsActionTypes.REPLACE_SETTINGS:
      return shouldEraseSyncData(action, globalState.sync.settings) ? Set() : state;

    default:
      return state;
  }
};

const finalReducer = combineReducersWithGlobal(immutableRecords.TimelineSyncRecord(), {
  settings,
  lastSyncRep,
  externalRep,
  status,
  userActions,
  localToExternalIds,
  customFieldsIdsMap,
  userCreatedLinks,
  fileMissing,
  isScrivener2,
  ignoreExternalIds
});

export default finalReducer;
